import React, { useState, useEffect, useCallback } from 'react';
import { FaTrash, FaDownload, FaRedo, FaSearch, FaFilter, FaTimes, FaCheck, FaRobot } from 'react-icons/fa';
import { useWebSocket } from '../contexts/WebSocketContext';
import { getServerURL } from '../config/api';
import './ScannedBarcodes.css';

const ScannedBarcodes = () => {
  const [scannedBarcodes, setScannedBarcodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedBarcode, setSelectedBarcode] = useState(null);
  const [showBarcodeDialog, setShowBarcodeDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  const [scannerFilter, setScannerFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(100);
  const [analyzingIds, setAnalyzingIds] = useState([]);

  // Fetch scanned barcodes from database
  const fetchScannedBarcodes = useCallback(async (isBackground = false) => {
    if (!isBackground) {
      setLoading(true);
    } else {
      setIsRefreshing(true);
    }

    try {

      const serverURL = getServerURL();
      const token = localStorage.getItem('robridge_token');

      const response = await fetch(`${serverURL}/api/barcodes/scanned?limit=1000&t=${Date.now()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();

      if (data.success) {
        const barcodes = data.barcodes || [];
        setScannedBarcodes(barcodes);
      } else {
        console.error('API returned error:', data.error);
        setScannedBarcodes([]);
      }
    } catch (error) {
      console.error('Error fetching scanned barcodes:', error);
      setScannedBarcodes([]);
    } finally {
      if (!isBackground) {
        setLoading(false);
      }
      setIsRefreshing(false);
    }
  }, []);

  // Load scanned barcodes on component mount and set up auto-refresh
  const { socket } = useWebSocket();

  useEffect(() => {
    fetchScannedBarcodes();

    // Auto-refresh every 5 seconds
    const refreshInterval = setInterval(() => {
      fetchScannedBarcodes(true);
    }, 5000);

    return () => clearInterval(refreshInterval);
  }, [fetchScannedBarcodes]);

  // Listen for real-time scans via WebSocket
  useEffect(() => {
    if (socket) {
      const handleNewScan = (scanData) => {        // Convert timestamp to ISO string if it's a Unix timestamp (number)
        // ESP32 sends millis() which is milliseconds since boot, not Unix timestamp
        // If timestamp is invalid (before year 2000 = 946684800000ms), use current time
        let timestamp = scanData.timestamp || new Date().toISOString();
        if (typeof timestamp === 'number') {
          // Check if timestamp is valid (after Jan 1, 2000)
          if (timestamp < 946684800000) {
            console.log(`⚠️  Invalid timestamp from ESP32 (millis): ${timestamp} - using current time`);
            timestamp = new Date().toISOString();
          } else {
            timestamp = new Date(timestamp).toISOString();
          }
        }

        // Skip OUT scans - they should not appear in Scanned Barcodes page
        if (scanData.isOutScan) {
          console.log('🔍 Skipping OUT scan - not displaying in Scanned Barcodes page');
          return;
        }

        const newBarcode = {
          id: scanData.id || `temp_${Date.now()}`,
          barcode_data: scanData.barcodeData,
          barcode_type: scanData.scanType,
          source: 'ESP32',
          device_id: scanData.deviceId,
          created_at: timestamp,
          metadata: JSON.stringify({
            aiAnalysis: scanData.aiAnalysis,
            deviceName: scanData.deviceName,
            description: scanData.aiAnalysis?.description || ''
          }),
          category: scanData.aiAnalysis?.category || 'Scanning...',
          product_name: scanData.aiAnalysis?.title || 'Processing...',
          price: 0
        };

        setScannedBarcodes(prev => {
          if (prev.some(b => b.id === newBarcode.id)) return prev;
          return [newBarcode, ...prev];
        });
      };

      socket.on('esp32_barcode_scan', handleNewScan);

      return () => {
        socket.off('esp32_barcode_scan', handleNewScan);
      };
    }
  }, [socket]);

  // Handle Manual AI Analysis
  const handleAnalyzeClick = async (barcodeId) => {
    try {
      setAnalyzingIds(prev => [...prev, barcodeId]);

      const serverURL = getServerURL();
      const token = localStorage.getItem('robridge_token');

      const response = await fetch(`${serverURL}/api/barcodes/${barcodeId}/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setScannedBarcodes(prev => prev.map(b => {
          if (b.id === barcodeId) {
            let currentMetadata = typeof b.metadata === 'string'
              ? JSON.parse(b.metadata || '{}')
              : (b.metadata || {});

            currentMetadata.aiAnalysis = data.aiAnalysis;
            currentMetadata.description = data.aiAnalysis.description;

            return {
              ...b,
              metadata: JSON.stringify(currentMetadata),
              product_name: data.aiAnalysis.title || b.product_name || 'Processed Product',
              category: data.aiAnalysis.category || b.category || 'Scanned',
              description: data.aiAnalysis.description
            };
          }
          return b;
        }));

        if (selectedBarcode && selectedBarcode.id === barcodeId) {
          setSelectedBarcode(prev => {
            let currentMetadata = typeof prev.metadata === 'string'
              ? JSON.parse(prev.metadata || '{}')
              : (prev.metadata || {});

            currentMetadata.aiAnalysis = data.aiAnalysis;
            currentMetadata.description = data.aiAnalysis.description;

            return {
              ...prev,
              metadata: JSON.stringify(currentMetadata),
              product_name: data.aiAnalysis.title || prev.product_name,
              category: data.aiAnalysis.category || prev.category,
              description: data.aiAnalysis.description
            }
          });
        }
      } else {
        alert('Analysis failed: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error triggering analysis:', error);
      alert('Failed to trigger analysis. Please check your connection.');
    } finally {
      setAnalyzingIds(prev => prev.filter(id => id !== barcodeId));
    }
  };

  const handleBarcodeClick = (barcode) => {
    setSelectedBarcode(barcode);
    setShowBarcodeDialog(true);
  };

  const closeBarcodeDialog = () => {
    setShowBarcodeDialog(false);
    setSelectedBarcode(null);
  };

  const saveScannedBarcode = async (barcode) => {
    try {
      let metadata = {};
      try {
        metadata = typeof barcode.metadata === 'string' ? JSON.parse(barcode.metadata) : barcode.metadata;
      } catch (e) {
        metadata = {};
      }

      const serverURL = getServerURL();
      const token = localStorage.getItem('robridge_token');

      const response = await fetch(`${serverURL}/api/save-scan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          barcode_data: barcode.barcode_data,
          barcode_type: barcode.barcode_type || 'ESP32_SCAN',
          source: 'ESP32',
          device_id: barcode.device_id || metadata.deviceId || 'ESP32_GM77_SCANNER_001',
          product_name: barcode.product_name || 'Unknown Product',
          category: barcode.category || 'Unknown',
          price: barcode.price || 0,
          description: metadata.description || metadata.productDetails || '',
          created_at: barcode.created_at,
          metadata: {
            originalId: barcode.id,
            originalTimestamp: barcode.created_at,
            deviceId: barcode.device_id || metadata.deviceId || 'ESP32_GM77_SCANNER_001',
            deviceName: metadata.deviceName || 'ESP32-GM77-Barcode-Scanner',
            aiAnalysis: metadata.aiAnalysis || null,
            savedFromScannedBarcodes: true
          }
        })
      });

      const result = await response.json();

      if (result.success) {
        alert('✅ Scan saved successfully! View in "Saved Scans" page.');
      } else {
        if (result.duplicate) {
          alert('⚠️ ' + result.error + '\n\nLast saved: ' + new Date(result.lastSaved).toLocaleString());
        } else {
          alert('❌ Failed to save scan: ' + (result.error || 'Unknown error'));
        }
      }
    } catch (error) {
      console.error('Error saving scanned barcode:', error);
      alert('❌ Error saving scan. Please ensure the server is running.');
    }
  };

  const deleteScannedBarcode = async (barcodeId) => {
    if (!window.confirm('Are you sure you want to delete this scanned barcode?')) {
      return;
    }

    try {
      const serverURL = getServerURL();
      const token = localStorage.getItem('robridge_token');

      const response = await fetch(`${serverURL}/api/barcodes/${barcodeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();

      if (result.success) {
        setScannedBarcodes(prev => prev.filter(barcode => barcode.id !== barcodeId));
        alert('Scanned barcode deleted successfully!');
      } else {
        alert('Failed to delete scanned barcode: ' + result.error);
      }
    } catch (error) {
      console.error('Error deleting scanned barcode:', error);
      alert('Error deleting scanned barcode. Please try again.');
    }
  };

  const uniqueScanners = [...new Set(scannedBarcodes.map(b => b.device_id || b.source || 'UNKNOWN'))];

  const filteredScannedBarcodes = scannedBarcodes
    .filter(barcode => {
      const matchesSearch = searchTerm === '' ||
        barcode.barcode_data.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesScanner = scannerFilter === 'all' ||
        (barcode.device_id || barcode.source || 'UNKNOWN') === scannerFilter;
      return matchesSearch && matchesScanner;
    })
    .sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

  const totalPages = Math.ceil(filteredScannedBarcodes.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentScannedBarcodes = filteredScannedBarcodes.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortOrder, scannerFilter]);

  const clearAllScannedBarcodes = async () => {
    if (!window.confirm('⚠️ Are you sure you want to delete ALL scanned barcodes? This cannot be undone!')) {
      return;
    }
    try {
      const serverURL = getServerURL();
      const token = localStorage.getItem('robridge_token');
      const response = await fetch(`${serverURL}/api/barcodes/clear`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        setScannedBarcodes([]);
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to clear scanned barcodes');
      }
    } catch (error) {
      console.error('Error clearing scanned barcodes:', error);
      alert(`❌ Error clearing scanned barcodes: ${error.message}`);
    }
  };

  const exportToCSV = () => {
    const csvContent = [
      ['Barcode Data', 'Source', 'Date', 'Time'],
      ...filteredScannedBarcodes.map(barcode => [
        barcode.barcode_data,
        barcode.device_id || barcode.source || 'UNKNOWN',
        new Date(barcode.created_at).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        }),
        new Date(barcode.created_at).toLocaleTimeString('en-GB', {
          hour: '2-digit',
          minute: '2-digit'
        })
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scanned_barcodes_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Save All Scanned Barcodes (bulk save)
  const saveAllScans = async () => {
    // Get IDs of filtered unsaved scans (treat NULL as unsaved)
    const unsavedScans = filteredScannedBarcodes.filter(b => b.is_saved !== true);

    console.log('🔍 Total filtered scans:', filteredScannedBarcodes.length);
    console.log('🔍 Unsaved scans:', unsavedScans.length);
    console.log('🔍 Sample scan is_saved values:', filteredScannedBarcodes.slice(0, 3).map(b => ({ id: b.id, is_saved: b.is_saved })));

    if (unsavedScans.length === 0) {
      alert('No unsaved scans to save!');
      return;
    }

    if (!window.confirm(`Save ${unsavedScans.length} unsaved scan(s) to Saved Scans?`)) {
      return;
    }

    try {
      const serverURL = getServerURL();
      const token = localStorage.getItem('robridge_token');

      const scanIds = unsavedScans.map(b => b.id);
      console.log('📤 Sending scan IDs:', scanIds);

      const response = await fetch(`${serverURL}/api/barcodes/save-all`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ scanIds })
      });

      const data = await response.json();
      console.log('📥 Response:', data);

      if (data.success) {
        if (data.errors && data.errors.length > 0) {
          console.error('⚠️ Some scans failed to save:', data.errors);
          alert(`⚠️ Saved ${data.savedCount} scan(s), but ${data.errors.length} failed.\n\nErrors:\n${data.errors.map(e => `ID ${e.id}: ${e.error}`).join('\n')}`);
        } else {
          alert(`✅ Successfully saved ${data.savedCount} scan(s)!`);
        }
        // Refresh data to update is_saved flags
        fetchScannedBarcodes(false);
      } else {
        alert('Failed to save scans: ' + data.error);
      }
    } catch (error) {
      console.error('Error saving scans:', error);
      alert('Error saving scans. Please try again.');
    }
  };

  // Re-sync saved scans (two-way sync)
  const resyncSavedScans = async () => {
    if (!window.confirm('🔄 This will sync scans between Scanned Barcodes and Saved Scans.\n\nIf you deleted a scan from Saved Scans, the button will change back to "Save" so you can save it again.\n\nContinue?')) {
      return;
    }

    try {
      const serverURL = getServerURL();
      const token = localStorage.getItem('robridge_token');

      const response = await fetch(`${serverURL}/api/barcodes/resync-saved`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        alert(`✅ ${data.message}\n\nButtons changed from "Saved" to "Save" for deleted scans. You can now save them again!`);
        // Refresh data to update button states
        fetchScannedBarcodes(false);
      } else {
        alert('❌ Failed to re-sync: ' + data.error);
      }
    } catch (error) {
      console.error('Error re-syncing:', error);
      alert('❌ Error re-syncing scans.');
    }
  };


  return (
    <div className="scanned-barcodes-page">
      <div className="page-header">
        <h1>Scanned Barcodes</h1>
        <p>View and manage all your scanned barcodes from your devices</p>
      </div>

      <div className="controls-section">
        <div className="search-filter-container">
          <div className="search-input">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search scanned barcodes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filter-dropdown">
            <FaFilter className="filter-icon" />
            <select
              value={scannerFilter}
              onChange={(e) => setScannerFilter(e.target.value)}
            >
              <option value="all">All Scanners</option>
              {uniqueScanners.map(scanner => (
                <option key={scanner} value={scanner}>{scanner}</option>
              ))}
            </select>
          </div>
          <div className="filter-dropdown">
            <FaFilter className="filter-icon" />
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
            </select>
          </div>
        </div>
        <div className="action-buttons">
          <button
            className="btn btn-secondary"
            onClick={() => fetchScannedBarcodes(false)}
            disabled={loading}
          >
            <FaRedo />
            {loading ? 'Loading...' : 'Refresh'}
          </button>
          <button
            className="btn btn-primary"
            onClick={exportToCSV}
            disabled={filteredScannedBarcodes.length === 0}
          >
            <FaDownload />
            Export CSV
          </button>

          <button
            className="btn btn-success"
            onClick={saveAllScans}
            disabled={filteredScannedBarcodes.filter(b => b.is_saved !== true).length === 0}
          >
            <FaCheck />
            Save All ({filteredScannedBarcodes.filter(b => b.is_saved !== true).length} unsaved)
          </button>
          <button
            className="btn btn-warning"
            onClick={resyncSavedScans}
            style={{ backgroundColor: '#FF9800', borderColor: '#FF9800' }}
            title="Reset 'Saved' buttons to 'Save' for scans deleted from Saved Scans page"
          >
            🔄 Re-sync
          </button>
          <button
            className="btn btn-danger"
            onClick={clearAllScannedBarcodes}
            disabled={scannedBarcodes.length === 0}
          >
            <FaTrash />
            Clear All
          </button>
        </div>
      </div>

      <div className="scanned-barcodes-content">
        <div className="barcodes-summary">
          <div className="summary-info">
            <h2>Scanned Barcodes History</h2>
            <span className="total-count">
              {loading ? 'Loading...' : `${filteredScannedBarcodes.length} of ${scannedBarcodes.length} scanned barcodes`}
              {!loading && totalPages > 1 && (
                <span className="page-info"> • Page {currentPage} of {totalPages}</span>
              )}
            </span>
          </div>
        </div>

        <div className="barcodes-table-container">
          <table className="barcodes-table">
            <thead>
              <tr>
                <th>Barcode Data</th>
                <th>Source</th>
                <th>Scanned Date</th>
                <th>Scanned Time</th>
                <th className="action-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="loading-cell" style={{
                    textAlign: 'center',
                    padding: '60px 40px',
                    color: '#5F6368',
                    backgroundColor: '#FFFFFF',
                    background: '#FFFFFF'
                  }}>
                    <div className="spinner-small" style={{
                      display: 'inline-block',
                      width: '20px',
                      height: '20px',
                      border: '2px solid #E3821E',
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      marginRight: '10px',
                      verticalAlign: 'middle'
                    }}></div>
                    Loading scanned barcodes...
                  </td>
                </tr>
              ) : filteredScannedBarcodes.length > 0 ? (
                currentScannedBarcodes.map((barcode, index) => (
                  <tr
                    key={`${barcode.id}-${index}-${Date.now()}`}
                    className="barcode-row"
                    onClick={() => handleBarcodeClick(barcode)}
                  >
                    <td className="barcode-data">
                      <span className="barcode-text">
                        {barcode.barcode_data}
                      </span>
                    </td>
                    <td className="barcode-source">
                      <span className={`source-badge source-robridgeai`}>
                        {barcode.device_id || barcode.source || 'UNKNOWN'}
                      </span>
                    </td>
                    <td className="barcode-date">
                      {new Date(barcode.created_at).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })}
                    </td>
                    <td className="barcode-time">
                      {new Date(barcode.created_at).toLocaleTimeString('en-GB', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                    <td className="action-cell">
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button
                          className={barcode.is_saved === true ? "saved-button" : "save-button"}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (barcode.is_saved !== true) {
                              saveScannedBarcode(barcode);
                            }
                          }}
                          disabled={barcode.is_saved === true}
                          title={barcode.is_saved === true ? "Already saved" : "Save this scan to Saved Scans"}
                        >
                          <FaCheck />
                          {barcode.is_saved === true ? 'Saved' : 'Save'}
                        </button>
                        <button
                          className="delete-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteScannedBarcode(barcode.id);
                          }}
                          title="Delete scanned barcode"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5">
                    <div className="empty-state">
                      <div className="empty-icon">
                        <FaSearch />
                      </div>
                      <h3>No Scanned Barcodes Found</h3>
                      <p>
                        {searchTerm || scannerFilter !== 'all'
                          ? 'No scanned barcodes match your current search or filter criteria.'
                          : 'No barcodes have been scanned yet. Start scanning with your ESP32 device to see barcode history here.'
                        }
                      </p>
                      {(searchTerm || scannerFilter !== 'all') && (
                        <button
                          className="btn btn-secondary"
                          onClick={() => {
                            setSearchTerm('');
                            setScannerFilter('all');
                            setSortOrder('newest');
                          }}
                        >
                          Clear Filters
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {!loading && totalPages > 1 && (
            <div className="pagination-container" style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px 24px',
              borderTop: '1px solid #E8EAED'
            }}>
              <div className="pagination-info" style={{ color: '#5F6368', fontSize: '14px' }}>
                Showing {startIndex + 1}-{Math.min(endIndex, filteredScannedBarcodes.length)} of {filteredScannedBarcodes.length} scanned barcodes
              </div>
              <div className="pagination-controls" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  title="Previous page"
                >
                  Previous
                </button>
                <span style={{ color: '#5F6368', fontSize: '14px' }}>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  className="btn btn-secondary"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  title="Next page"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showBarcodeDialog && selectedBarcode && (
        <div className="barcode-dialog-overlay" onClick={closeBarcodeDialog}>
          <div className="barcode-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="barcode-dialog-header">
              <h2>Scanned Barcode Details</h2>
              <button className="close-btn" onClick={closeBarcodeDialog}>
                <FaTimes />
              </button>
            </div>
            <div className="barcode-dialog-body">
              <div className="scan-info-section">
                <h3>Basic Information</h3>
                <div className="info-grid-two-columns">
                  <div className="info-column">
                    <div className="info-field">
                      <label>Source</label>
                      <span className="info-value source-badge">
                        {(() => {
                          try {
                            const metadata = typeof selectedBarcode.metadata === 'string'
                              ? JSON.parse(selectedBarcode.metadata)
                              : selectedBarcode.metadata;
                            return metadata?.deviceName || selectedBarcode.device_id || selectedBarcode.source || 'Unknown Device';
                          } catch (e) {
                            return selectedBarcode.device_id || selectedBarcode.source || 'Unknown Device';
                          }
                        })()}
                      </span>
                    </div>
                  </div>
                  <div className="info-column">
                    <div className="info-field">
                      <label>Time Info</label>
                      <span className="info-value">
                        {new Date(selectedBarcode.created_at).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="barcode-data-section">
                <h3>Barcode Data</h3>
                <div className="barcode-data-container">
                  <span className="barcode-data-text">{selectedBarcode.barcode_data}</span>
                </div>
              </div>

              <div className="description-section">
                <h3>🤖 AI Analysis</h3>
                <div className="description-container">
                  <div className="description-text">
                    {(() => {
                      try {
                        let description = 'No description available';
                        let hasAI = false;
                        let metadata = null;
                        if (selectedBarcode.metadata) {
                          metadata = typeof selectedBarcode.metadata === 'string'
                            ? JSON.parse(selectedBarcode.metadata)
                            : selectedBarcode.metadata;
                          if (metadata.aiAnalysis?.description) {
                            description = metadata.aiAnalysis.description;
                            hasAI = true;
                          } else if (metadata.aiAnalysis?.description_short) {
                            description = metadata.aiAnalysis.description_short;
                            hasAI = true;
                          } else if (metadata.description && metadata.description !== 'No description available') {
                            description = metadata.description;
                            hasAI = true;
                          }
                        }
                        const isBasicScan = metadata?.aiAnalysis?.noAI === true ||
                          metadata?.aiAnalysis?.description?.includes('Basic scan without AI analysis') ||
                          metadata?.description?.includes('Basic scan without AI analysis') ||
                          metadata?.description?.includes('No AI analysis available') ||
                          metadata?.description?.includes('Basic scan - no AI analysis');
                        const renderButton = () => (
                          <div className="ai-analysis-trigger" style={{ marginTop: '0' }}>
                            <p className="description-placeholder">
                              {(hasAI && isBasicScan)
                                ? 'Basic details shown above. Analyse with AI to get more details on the scanned data'
                                : 'No AI analysis performed yet.'}
                            </p>
                            <button
                              className="btn-ai-analyze"
                              onClick={() => handleAnalyzeClick(selectedBarcode.id)}
                              disabled={analyzingIds.includes(selectedBarcode.id)}
                            >
                              {analyzingIds.includes(selectedBarcode.id) ? (
                                <>
                                  <div className="spinner-micro"></div>
                                  Analyzing...
                                </>
                              ) : (
                                <>
                                  <FaRobot /> Analyze with AI
                                </>
                              )}
                            </button>
                          </div>
                        );
                        if (hasAI && isBasicScan) return renderButton();
                        if (hasAI) return description;
                        return renderButton();
                      } catch (error) {
                        console.error('Error parsing metadata:', error);
                        return selectedBarcode.product_name || 'No description available';
                      }
                    })()}
                  </div>
                </div>
              </div>
            </div>
            <div className="barcode-dialog-footer">
              <button className="btn btn-success" onClick={() => {
                saveScannedBarcode(selectedBarcode);
                closeBarcodeDialog();
              }}>
                <FaCheck />
                Save to Saved Scans
              </button>
              <button className="btn btn-danger" onClick={() => {
                deleteScannedBarcode(selectedBarcode.id);
                closeBarcodeDialog();
              }}>
                <FaTrash />
                Delete Scanned Barcode
              </button>
              <button className="btn btn-secondary" onClick={closeBarcodeDialog}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {isRefreshing && (
        <div className="fixed-loader" style={{
          position: 'fixed',
          bottom: '24px',
          left: '300px',
          background: 'rgba(255, 255, 255, 0.9)',
          padding: '8px 16px',
          borderRadius: '24px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          zIndex: 1000,
          border: '1px solid #E8EAED',
          fontSize: '13px',
          color: '#5F6368',
          fontWeight: 500
        }}>
          <div className="spinner-small" style={{
            width: '14px',
            height: '14px',
            border: '2px solid #E3821E',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          Updating codes...
        </div>
      )}
    </div>
  );
};

export default ScannedBarcodes;
